import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { 
    Loader2, 
    ArrowLeft, 
    PlayCircle, 
    BookOpen, 
    Video,
    Palette,
    Boxes
} from 'lucide-react';
import api from '../lib/api';
import { formatPrice } from '../lib/utils';
import { SEO } from '../components/SEO';

// Helper to generate slug from name
const nameToSlug = (name) => name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');

// Default thumbnail
const DEFAULT_THUMBNAIL = 'https://images.unsplash.com/photo-1579783902614-a3fb3927b6a5?w=400&h=300&fit=crop';

export const InstructorPage = () => {
    const { slug } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchInstructor = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await api.get(`/instructors/${slug}/content`);
                setData(response.data);
            } catch (err) {
                console.error('Failed to fetch instructor:', err);
                setError(err.response?.data?.detail || 'Failed to load instructor');
            } finally {
                setLoading(false);
            }
        };

        if (slug) {
            fetchInstructor();
        }
    }, [slug]);

    if (loading) {
        return (
            <div className="min-h-[50vh] flex items-center justify-center" data-testid="instructor-loading">
                <Loader2 className="h-8 w-8 animate-spin text-accent" />
            </div>
        );
    }

    if (error || !data) {
        return (
            <div className="section-gap" data-testid="instructor-error">
                <div className="container-main text-center">
                    <h1 className="text-2xl font-semibold mb-4">Instructor Not Found</h1>
                    <p className="text-muted-foreground mb-6">{error || 'Unable to load instructor information.'}</p>
                    <Button asChild variant="outline">
                        <Link to="/about">
                            <ArrowLeft className="h-4 w-4 mr-2" />
                            Back to About
                        </Link>
                    </Button>
                </div>
            </div>
        );
    }

    const { instructor, courses, workshops, sculptures, total_videos } = data;

    // Generate SEO description
    const seoDescription = instructor.bio 
        ? instructor.bio.substring(0, 160) + '...'
        : `Learn from ${instructor.name}, professional artist and instructor at Zarolla. ${total_videos} video lessons available.`;

    // Generate keywords based on instructor
    const seoKeywords = [
        `${instructor.name} art`,
        `${instructor.name} drawing`,
        instructor.name.toLowerCase().includes('orbik') ? 'Glen Orbik drawing' : null,
        instructor.name.toLowerCase().includes('pro') ? 'Tony Pro painting' : null,
        'online art classes',
        'professional art instruction',
    ].filter(Boolean);

    // Person schema for instructor
    const personSchema = {
        "@context": "https://schema.org",
        "@type": "Person",
        "name": instructor.name,
        "description": instructor.bio,
        "image": instructor.image,
        "url": `https://zarolla.com/instructor/${slug}`,
        "jobTitle": "Art Instructor",
        "worksFor": {
            "@type": "Organization",
            "name": "Zarolla Online Art Academy"
        },
        "knowsAbout": [
            "Oil Painting",
            "Portrait Painting", 
            "Figure Drawing",
            "Classical Art"
        ],
        "sameAs": []
    };

    return (
        <>
            {/* Dynamic SEO with Person Schema */}
            <SEO
                title={`${instructor.name} | Art Instructor`}
                description={seoDescription}
                image={instructor.image}
                url={`/instructor/${slug}`}
                type="profile"
                keywords={seoKeywords}
                instructor={{
                    name: instructor.name,
                    bio: instructor.bio,
                    image: instructor.image,
                }}
                structuredData={personSchema}
            />

            <div data-testid="instructor-page">
            {/* Hero Section */}
            <section className="section-gap bg-secondary/30">
                <div className="container-main">
                    <Link 
                        to="/about" 
                        className="inline-flex items-center text-sm text-muted-foreground hover:text-accent mb-6"
                    >
                        <ArrowLeft className="h-4 w-4 mr-1" />
                        Back to About
                    </Link>
                    
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
                        {/* Photo */}
                        <div className="lg:col-span-1">
                            <div className="aspect-[3/4] rounded-lg overflow-hidden bg-secondary shadow-lg">
                                <img
                                    src={instructor.image || DEFAULT_THUMBNAIL}
                                    alt={instructor.name}
                                    className="w-full h-full object-cover"
                                    onError={(e) => e.target.src = DEFAULT_THUMBNAIL}
                                />
                            </div>
                        </div>
                        
                        {/* Bio */}
                        <div className="lg:col-span-2">
                            <span className="text-xs font-semibold uppercase tracking-widest text-accent">
                                Instructor
                            </span>
                            <h1 className="font-serif text-4xl sm:text-5xl font-semibold text-primary leading-tight mt-2">
                                {instructor.name}
                            </h1>
                            
                            {/* Stats */}
                            <div className="flex flex-wrap gap-4 mt-6">
                                {courses.length > 0 && (
                                    <Badge variant="secondary" className="text-sm py-1 px-3">
                                        <BookOpen className="h-4 w-4 mr-1" />
                                        {courses.length} Course{courses.length !== 1 ? 's' : ''}
                                    </Badge>
                                )}
                                {workshops.length > 0 && (
                                    <Badge variant="secondary" className="text-sm py-1 px-3">
                                        <Palette className="h-4 w-4 mr-1" />
                                        {workshops.length} Workshop{workshops.length !== 1 ? 's' : ''}
                                    </Badge>
                                )}
                                {sculptures.length > 0 && (
                                    <Badge variant="secondary" className="text-sm py-1 px-3">
                                        <Boxes className="h-4 w-4 mr-1" />
                                        {sculptures.length} Sculpture Video{sculptures.length !== 1 ? 's' : ''}
                                    </Badge>
                                )}
                                <Badge variant="outline" className="text-sm py-1 px-3">
                                    <Video className="h-4 w-4 mr-1" />
                                    {total_videos} Total Video{total_videos !== 1 ? 's' : ''}
                                </Badge>
                            </div>
                            
                            {/* Bio Text */}
                            <div className="mt-8 prose prose-slate max-w-none">
                                {instructor.bio.split('\n\n').map((paragraph, idx) => (
                                    <p key={idx} className="text-muted-foreground leading-relaxed mb-4">
                                        {paragraph}
                                    </p>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Courses Section */}
            {courses.length > 0 && (
                <section className="section-gap" data-testid="instructor-courses">
                    <div className="container-main">
                        <div className="flex items-center gap-3 mb-8">
                            <BookOpen className="h-6 w-6 text-accent" />
                            <h2 className="font-serif text-3xl font-semibold">Courses</h2>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {courses.map((course) => (
                                <Card 
                                    key={course.course_id} 
                                    className="overflow-hidden hover:shadow-lg transition-shadow"
                                    data-testid={`course-card-${course.course_id}`}
                                >
                                    <div className="flex flex-col sm:flex-row">
                                        <div className="sm:w-48 flex-shrink-0">
                                            <div className="aspect-video sm:aspect-square bg-secondary">
                                                <img
                                                    src={course.thumbnail || `https://vumbnail.com/${course.vimeo_id || '123456789'}.jpg`}
                                                    alt={course.title}
                                                    className="w-full h-full object-cover"
                                                    onError={(e) => e.target.src = DEFAULT_THUMBNAIL}
                                                />
                                            </div>
                                        </div>
                                        <CardContent className="flex-1 p-4">
                                            <h3 className="font-semibold text-lg mb-2 line-clamp-2">
                                                {course.title}
                                            </h3>
                                            <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                                                {course.description || `A comprehensive course with ${course.lesson_count || '?'} lessons.`}
                                            </p>
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-2">
                                                    <Badge variant="outline">
                                                        <PlayCircle className="h-3 w-3 mr-1" />
                                                        {course.lesson_count || 0} lessons
                                                    </Badge>
                                                    {course.cost && (
                                                        <span className="text-sm font-semibold text-accent">
                                                            {formatPrice(course.cost)}
                                                        </span>
                                                    )}
                                                </div>
                                                <Button asChild size="sm" className="bg-accent text-accent-foreground hover:bg-accent/90">
                                                    <Link to={`/courses/${course.course_id}`}>
                                                        View Course
                                                    </Link>
                                                </Button>
                                            </div>
                                        </CardContent>
                                    </div>
                                </Card>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Workshops Section */}
            {workshops.length > 0 && (
                <section className="section-gap bg-secondary/20" data-testid="instructor-workshops">
                    <div className="container-main">
                        <div className="flex items-center gap-3 mb-8">
                            <Palette className="h-6 w-6 text-accent" />
                            <h2 className="font-serif text-3xl font-semibold">Painting Workshops</h2>
                        </div>
                        
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            {workshops.map((video) => (
                                <Card 
                                    key={video.id} 
                                    className="overflow-hidden hover:shadow-lg transition-shadow"
                                    data-testid={`workshop-card-${video.id}`}
                                >
                                    <div className="aspect-video bg-secondary relative">
                                        <img
                                            src={video.thumbnail || `https://vumbnail.com/${video.vimeo_id || '123456789'}.jpg`}
                                            alt={video.title}
                                            className="w-full h-full object-cover"
                                            onError={(e) => e.target.src = DEFAULT_THUMBNAIL}
                                        />
                                        <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                                            <PlayCircle className="h-12 w-12 text-white" />
                                        </div>
                                    </div>
                                    <CardContent className="p-4">
                                        <h3 className="font-semibold mb-2 line-clamp-2">
                                            {video.title}
                                        </h3>
                                        <div className="flex items-center justify-between">
                                            {video.cost && (
                                                <span className="text-sm font-semibold text-accent">
                                                    {formatPrice(video.cost)}
                                                </span>
                                            )}
                                            <Button asChild size="sm" variant="outline">
                                                <Link to={`/videos/${video.id}`}>
                                                    View Details
                                                </Link>
                                            </Button>
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Sculptures Section */}
            {sculptures.length > 0 && (
                <section className="section-gap" data-testid="instructor-sculptures">
                    <div className="container-main">
                        <div className="flex items-center gap-3 mb-8">
                            <Boxes className="h-6 w-6 text-accent" />
                            <h2 className="font-serif text-3xl font-semibold">Sculpture Videos</h2>
                        </div>
                        
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            {sculptures.map((video) => (
                                <Card 
                                    key={video.id} 
                                    className="overflow-hidden hover:shadow-lg transition-shadow"
                                    data-testid={`sculpture-card-${video.id}`}
                                >
                                    <div className="aspect-video bg-secondary relative">
                                        <img
                                            src={video.thumbnail || `https://vumbnail.com/${video.vimeo_id || '123456789'}.jpg`}
                                            alt={video.title}
                                            className="w-full h-full object-cover"
                                            onError={(e) => e.target.src = DEFAULT_THUMBNAIL}
                                        />
                                        <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                                            <PlayCircle className="h-12 w-12 text-white" />
                                        </div>
                                    </div>
                                    <CardContent className="p-4">
                                        <h3 className="font-semibold mb-2 line-clamp-2">
                                            {video.title}
                                        </h3>
                                        <div className="flex items-center justify-between">
                                            {video.cost && (
                                                <span className="text-sm font-semibold text-accent">
                                                    {formatPrice(video.cost)}
                                                </span>
                                            )}
                                            <Button asChild size="sm" variant="outline">
                                                <Link to={`/videos/${video.id}`}>
                                                    View Details
                                                </Link>
                                            </Button>
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* CTA Section */}
            <section className="py-16 bg-accent/10">
                <div className="container-main text-center">
                    <h2 className="font-serif text-2xl font-semibold mb-4">
                        Ready to learn from {instructor.name}?
                    </h2>
                    <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
                        Join thousands of artists who have transformed their skills with our comprehensive video courses.
                    </p>
                    <div className="flex flex-wrap gap-4 justify-center">
                        {courses.length > 0 && (
                            <Button asChild className="bg-accent text-accent-foreground hover:bg-accent/90">
                                <Link to={`/courses/${courses[0].course_id}`}>
                                    Start with {courses[0].title?.split(' with ')[0] || 'First Course'}
                                </Link>
                            </Button>
                        )}
                        <Button asChild variant="outline">
                            <Link to="/courses">
                                Browse All Courses
                            </Link>
                        </Button>
                    </div>
                </div>
            </section>
        </div>
        </>
    );
};

export default InstructorPage;
